import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { useSupabaseAsset } from '@/hooks/use-supabase-asset'
import { Skeleton } from '@/components/ui/skeleton'
import { Image } from '@/components/image'
import { SupabaseImage } from '@/components/supabase-image'
import { Video } from '@/components/video'

const ORG_ID = 'aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaab'
const WORKSPACE_ID = 'aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa01'
const GENERATIONS_BUCKET = 'generations'

const WEBP_IMAGE_PATH = `${ORG_ID}/${WORKSPACE_ID}/vto/result-1.jpg`
const ORIGIN_IMAGE_PATH = `${ORG_ID}/${WORKSPACE_ID}/vto/result-2.jpg`
const STORAGE_IMAGE_PATH = `${ORG_ID}/${WORKSPACE_ID}/vto/result-3.jpg`
const MISSING_IMAGE_PATH = `${ORG_ID}/${WORKSPACE_ID}/vto/missing-file.jpg`

const LOCAL_PNG_DATA =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII='

function useGeneratedVideoStream() {
  const stream = useMemo(() => {
    if (!('captureStream' in HTMLCanvasElement.prototype)) {
      return null
    }

    const canvas = document.createElement('canvas')
    canvas.width = 2
    canvas.height = 2

    const context = canvas.getContext('2d')
    if (!context) {
      return null
    }

    context.fillStyle = '#000'
    context.fillRect(0, 0, canvas.width, canvas.height)

    return canvas.captureStream(30)
  }, [])

  useEffect(() => {
    return () => {
      stream?.getTracks().forEach((track) => track.stop())
    }
  }, [stream])

  return stream
}

function E2EMediaPage() {
  const [showLocalSkeleton, setShowLocalSkeleton] = useState(true)
  const [missingImageError, setMissingImageError] = useState(false)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [posterVideoLoaded, setPosterVideoLoaded] = useState(false)
  const generatedVideoStream = useGeneratedVideoStream()
  const missingAsset = useSupabaseAsset(
    GENERATIONS_BUCKET,
    MISSING_IMAGE_PATH,
    {
      imageTransform: { quality: 85 },
    }
  )

  useEffect(() => {
    const timer = window.setTimeout(() => setShowLocalSkeleton(false), 2000)
    return () => window.clearTimeout(timer)
  }, [])

  return (
    <div className='space-y-10 p-6' data-testid='e2e-media-page'>
      <section className='space-y-4'>
        <h1 className='text-lg font-semibold'>E2E Media Playground</h1>
        <p className='text-muted-foreground text-sm'>
          This page is used by Playwright to validate media loading behavior.
        </p>
      </section>

      <section className='space-y-6'>
        <h2 className='text-sm font-semibold tracking-wide uppercase'>
          Images
        </h2>

        <div className='space-y-2' data-testid='local-image-success'>
          <p className='text-sm'>Local image with skeleton</p>
          <div className='relative h-24 w-24'>
            {showLocalSkeleton && (
              <Skeleton
                className='absolute inset-0'
                data-testid='local-image-skeleton'
              />
            )}
            <Image
              src={LOCAL_PNG_DATA}
              alt='Local test image'
              width={96}
              height={96}
              className='rounded-md'
              data-testid='local-image'
            />
          </div>
        </div>

        <div className='space-y-2' data-testid='local-image-error'>
          <p className='text-sm'>Broken image fallback</p>
          <Image
            src='/missing-local-image.jpg'
            alt='Broken test image'
            width={96}
            height={96}
            className='rounded-md'
          />
        </div>

        <div className='space-y-2' data-testid='supabase-image-webp'>
          <p className='text-sm'>Supabase image with transform (auto WebP)</p>
          <SupabaseImage
            bucket={GENERATIONS_BUCKET}
            path={WEBP_IMAGE_PATH}
            alt='Supabase WebP test image'
            width={200}
            height={200}
            className='rounded-md'
            data-testid='supabase-image-webp-img'
          />
        </div>

        <div className='space-y-2' data-testid='supabase-image-origin'>
          <p className='text-sm'>Supabase image with format=origin</p>
          <SupabaseImage
            bucket={GENERATIONS_BUCKET}
            path={ORIGIN_IMAGE_PATH}
            alt='Supabase origin format image'
            width={200}
            height={200}
            className='rounded-md'
            data-testid='supabase-image-origin-img'
            imageTransform={{ format: 'origin', quality: 85 }}
          />
        </div>

        <div className='space-y-2' data-testid='supabase-image-existing'>
          <p className='text-sm'>Supabase image from bucket</p>
          <SupabaseImage
            bucket={GENERATIONS_BUCKET}
            path={STORAGE_IMAGE_PATH}
            alt='Supabase storage image'
            width={200}
            height={200}
            className='rounded-md'
            data-testid='supabase-image-existing-img'
          />
        </div>

        <div className='space-y-2' data-testid='supabase-image-missing'>
          <p className='text-sm'>Missing Supabase image</p>
          <Image
            src={missingAsset.url ?? ''}
            alt='Missing supabase image'
            width={200}
            height={200}
            className='rounded-md'
            showError={missingImageError || Boolean(missingAsset.error)}
            onError={() => setMissingImageError(true)}
          />
        </div>
      </section>

      <section className='space-y-6'>
        <h2 className='text-sm font-semibold tracking-wide uppercase'>
          Lazy Loading
        </h2>
        <div className='space-y-6'>
          <SupabaseImage
            bucket={GENERATIONS_BUCKET}
            path={`${ORG_ID}/${WORKSPACE_ID}/vto/result-4.jpg`}
            alt='Lazy image 1'
            width={200}
            height={200}
            className='rounded-md'
            data-testid='lazy-image-1'
            loading='lazy'
          />
          <div className='h-[900px]' />
          <SupabaseImage
            bucket={GENERATIONS_BUCKET}
            path={`${ORG_ID}/${WORKSPACE_ID}/vto/result-5.jpg`}
            alt='Lazy image 2'
            width={200}
            height={200}
            className='rounded-md'
            data-testid='lazy-image-2'
            loading='lazy'
          />
          <div className='h-[900px]' />
          <SupabaseImage
            bucket={GENERATIONS_BUCKET}
            path={`${ORG_ID}/${WORKSPACE_ID}/vto/result-6.jpg`}
            alt='Lazy image 3'
            width={200}
            height={200}
            className='rounded-md'
            data-testid='lazy-image-3'
            loading='lazy'
          />
        </div>
      </section>

      <section className='space-y-6'>
        <h2 className='text-sm font-semibold tracking-wide uppercase'>
          Videos
        </h2>

        <div className='space-y-2' data-testid='video-success'>
          <p className='text-sm'>Generated video</p>
          <div className='relative h-[180px] w-[320px]'>
            {!videoLoaded && (
              <Skeleton
                className='absolute inset-0'
                data-testid='video-success-skeleton'
              />
            )}
            {generatedVideoStream && (
              <Video
                src=''
                srcObject={generatedVideoStream}
                width={320}
                height={180}
                className='rounded-md'
                data-testid='video-success-element'
                onLoadedData={() => {
                  window.setTimeout(() => setVideoLoaded(true), 150)
                }}
              />
            )}
          </div>
        </div>

        <div className='space-y-2' data-testid='video-poster'>
          <p className='text-sm'>Video with poster</p>
          <div className='relative h-[180px] w-[320px]'>
            {!posterVideoLoaded && (
              <Skeleton
                className='absolute inset-0'
                data-testid='video-poster-skeleton'
              />
            )}
            {generatedVideoStream && (
              <Video
                src=''
                srcObject={generatedVideoStream}
                poster={LOCAL_PNG_DATA}
                width={320}
                height={180}
                className='rounded-md'
                data-testid='video-poster-element'
                onLoadedData={() => {
                  window.setTimeout(() => setPosterVideoLoaded(true), 150)
                }}
              />
            )}
          </div>
        </div>

        <div className='space-y-2' data-testid='video-error'>
          <p className='text-sm'>Broken video fallback</p>
          <Video
            src='/missing-video.mp4'
            width={320}
            height={180}
            className={cn('rounded-md')}
          />
        </div>
      </section>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/e2e/media')({
  component: E2EMediaPage,
})
